'use client';

import React, { useState, useRef } from 'react';
import { Upload, FileText, CheckCircle, AlertTriangle, Clock, Trash2, Eye, Plus, X } from 'lucide-react';

interface CredentialFile {
  id: string;
  name: string;
  type: string;
  fileName: string;
  fileSize: string;
  issueDate: string;
  expiryDate: string;
  status: 'active' | 'expiring_soon' | 'expired' | 'pending_review';
  daysLeft: number;
}

const INITIAL_CREDENTIALS: CredentialFile[] = [
  { id: 'c1', name: 'RN License – Illinois', type: 'License', fileName: 'rn_license_il_2024.pdf', fileSize: '1.2 MB', issueDate: '2022-06-01', expiryDate: '2024-05-31', status: 'expired', daysLeft: -62 },
  { id: 'c2', name: 'BLS Certification', type: 'Certification', fileName: 'bls_cert_2024.pdf', fileSize: '0.8 MB', issueDate: '2023-08-15', expiryDate: '2024-08-15', status: 'expiring_soon', daysLeft: 14 },
  { id: 'c3', name: 'ACLS Certification', type: 'Certification', fileName: 'acls_cert_2024.pdf', fileSize: '1.1 MB', issueDate: '2023-09-01', expiryDate: '2025-09-01', status: 'active', daysLeft: 396 },
  { id: 'c4', name: 'Background Check', type: 'Background', fileName: 'bg_check_2024.pdf', fileSize: '0.5 MB', issueDate: '2024-01-10', expiryDate: '2025-01-10', status: 'active', daysLeft: 163 },
  { id: 'c5', name: 'TB Test Results', type: 'Health', fileName: 'tb_test_2024.pdf', fileSize: '0.3 MB', issueDate: '2024-03-01', expiryDate: '2025-03-01', status: 'active', daysLeft: 213 },
];

const STATUS_CONFIG = {
  active: { label: 'Active', icon: CheckCircle, color: 'text-success', bg: 'bg-success/10' },
  expiring_soon: { label: 'Expiring Soon', icon: AlertTriangle, color: 'text-warning', bg: 'bg-warning/10' },
  expired: { label: 'Expired', icon: AlertTriangle, color: 'text-danger', bg: 'bg-danger/10' },
  pending_review: { label: 'Pending Review', icon: Clock, color: 'text-primary', bg: 'bg-primary/10' },
};

const CREDENTIAL_TYPES = ['License', 'Certification', 'Training', 'Background', 'Health', 'Other'];

export default function CredentialUploads() {
  const [credentials, setCredentials] = useState<CredentialFile[]>(INITIAL_CREDENTIALS);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [newCred, setNewCred] = useState({ name: '', type: 'License', issueDate: '', expiryDate: '' });
  const [uploadedFileName, setUploadedFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setUploadedFileName(file.name);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setUploadedFileName(file.name);
  };

  const handleAddCredential = () => {
    if (!newCred.name || !newCred.issueDate || !newCred.expiryDate) return;
    const expiry = new Date(newCred.expiryDate);
    const ref = new Date('2024-08-01');
    const daysLeft = Math.round((expiry.getTime() - ref.getTime()) / (1000 * 60 * 60 * 24));
    const status: CredentialFile['status'] = daysLeft < 0 ? 'expired' : daysLeft <= 30 ? 'expiring_soon' : 'pending_review';
    const cred: CredentialFile = {
      id: `c${Date.now()}`,
      name: newCred.name,
      type: newCred.type,
      fileName: uploadedFileName || 'document.pdf',
      fileSize: '—',
      issueDate: newCred.issueDate,
      expiryDate: newCred.expiryDate,
      status,
      daysLeft,
    };
    setCredentials([cred, ...credentials]);
    setShowUploadModal(false);
    setNewCred({ name: '', type: 'License', issueDate: '', expiryDate: '' });
    setUploadedFileName('');
  };

  const handleDelete = (id: string) => {
    setCredentials(credentials.filter((c) => c.id !== id));
  };

  const counts = {
    active: credentials.filter((c) => c.status === 'active').length,
    expiring: credentials.filter((c) => c.status === 'expiring_soon').length,
    expired: credentials.filter((c) => c.status === 'expired').length,
    pending: credentials.filter((c) => c.status === 'pending_review').length,
  };

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Active', value: counts.active, color: 'text-success', bg: 'bg-success/10' },
          { label: 'Expiring Soon', value: counts.expiring, color: 'text-warning', bg: 'bg-warning/10' },
          { label: 'Expired', value: counts.expired, color: 'text-danger', bg: 'bg-danger/10' },
          { label: 'Pending Review', value: counts.pending, color: 'text-primary', bg: 'bg-primary/10' },
        ].map((s) => (
          <div key={s.label} className="bg-card border border-border rounded-xl p-4">
            <p className="text-xs text-muted-foreground font-500">{s.label}</p>
            <p className={`text-2xl font-700 mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Credential list */}
      <div className="bg-card border border-border rounded-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-sm font-700 text-foreground">My Credentials</h2>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-500 hover:bg-primary/90 transition-colors"
          >
            <Plus size={13} />
            Upload Credential
          </button>
        </div>
        <div className="divide-y divide-border">
          {credentials.map((cred) => {
            const cfg = STATUS_CONFIG[cred.status];
            const StatusIcon = cfg.icon;
            return (
              <div key={cred.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-muted/30 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                  <FileText size={16} className="text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-600 text-foreground">{cred.name}</p>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{cred.type}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{cred.fileName} · {cred.fileSize}</p>
                </div>
                <div className="hidden sm:block text-right text-xs text-muted-foreground">
                  <p>Issued: {cred.issueDate}</p>
                  <p>Expires: {cred.expiryDate}</p>
                </div>
                <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-500 ${cfg.bg} ${cfg.color} flex-shrink-0`}>
                  <StatusIcon size={11} />
                  {cfg.label}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button className="p-1.5 rounded-md hover:bg-muted text-muted-foreground transition-colors" title="View">
                    <Eye size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(cred.id)}
                    className="p-1.5 rounded-md hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card border border-border rounded-xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="text-sm font-700 text-foreground">Upload Credential</h3>
              <button onClick={() => setShowUploadModal(false)} className="p-1 rounded-md hover:bg-muted text-muted-foreground">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                  dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'
                }`}
              >
                <Upload size={24} className="mx-auto text-muted-foreground mb-2" />
                {uploadedFileName ? (
                  <p className="text-sm font-500 text-foreground">{uploadedFileName}</p>
                ) : (
                  <>
                    <p className="text-sm font-500 text-foreground">Drop file here or click to browse</p>
                    <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG up to 10 MB</p>
                  </>
                )}
                <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleFileSelect} />
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Credential Name *</label>
                  <input
                    type="text"
                    value={newCred.name}
                    onChange={(e) => setNewCred({ ...newCred, name: e.target.value })}
                    placeholder="e.g. RN License – Illinois"
                    className="mt-1 w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Type</label>
                  <select
                    value={newCred.type}
                    onChange={(e) => setNewCred({ ...newCred, type: e.target.value })}
                    className="mt-1 w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {CREDENTIAL_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Issue Date *</label>
                    <input
                      type="date"
                      value={newCred.issueDate}
                      onChange={(e) => setNewCred({ ...newCred, issueDate: e.target.value })}
                      className="mt-1 w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Expiry Date *</label>
                    <input
                      type="date"
                      value={newCred.expiryDate}
                      onChange={(e) => setNewCred({ ...newCred, expiryDate: e.target.value })}
                      className="mt-1 w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-border">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 rounded-lg border border-border text-sm font-500 text-muted-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCredential}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-500 hover:bg-primary/90 transition-colors"
              >
                <Upload size={14} />
                Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
